/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Input } from '@angular/core';
import { FormControl, FormGroup } from '@dm/core';
import { type Dtree, type FileSystem } from 'app/common/types/mo';

import { FileSystemStrService } from '../../file-system-str.service';
import { DEFAULT_UNIX_PERMISSIONS } from './unix-permisssion-config-card.const';
import { type UnixConfigCardEntryTypeE, UnixConfigCardActionTypeE } from './unix-permisssion-config-card.enum';


@Component({
    selector: 'app-unix-permission-config',
    templateUrl: './unix-permisssion-config-card.component.html',
    styleUrls: ['./unix-permisssion-config-card.component.scss'],
})
export class UnixPermissionConfigCardComponent implements OnInit {
    /**
     * 操作：创建、修改和详情
     */
    @Input() actionType: UnixConfigCardActionTypeE;
    /**
     * 入口：文件系统和dtree
     */
    @Input() entryType: UnixConfigCardEntryTypeE;
    /**
     * 传入数据
     */
    @Input() dtreeOrFsData: Dtree | FileSystem;

    vm: FormGroup;

    /**
     * 控制详情页面显示相关变量
     */
    isEmpty = false;
    isUserEmpty = false;
    isGroupEmpty = false;
    isOtherEmpty = false;

    unixPermissionData: string;
    showDetail = false;

    constructor(
        public str: FileSystemStrService,
    ) { }

    ngOnInit(): void {
        this.showDetail = this.actionType === UnixConfigCardActionTypeE.DETAIL;
        let permissionArr: string[] = this.dtreeOrFsData?.unixPermissions?.split('') || [];

        // 创建场景，如果dtreeOrFsData有值说明是模板创建，不需要设置成默认值
        if (this.actionType === UnixConfigCardActionTypeE.CREATE && !this.dtreeOrFsData) {
            permissionArr = DEFAULT_UNIX_PERMISSIONS.split('');
        }

        this.initVm(permissionArr);
        this.handleDetailShow(permissionArr);
    }

    /**
     * 初始化表单
     */
    initVm(permissionArr: string[]): void {
        this.vm = new FormGroup({
            user: new FormControl(this.renderDefaultValue(permissionArr[0])),
            userGroup: new FormControl(this.renderDefaultValue(permissionArr[1])),
            other: new FormControl(this.renderDefaultValue(permissionArr[2])),
        });
    }

    /**
     * 详情页面显示处理
     */
    handleDetailShow(permissionArr: string[]): void {
        this.isUserEmpty = this.isEmptyPermission(permissionArr[0]);
        this.isGroupEmpty = this.isEmptyPermission(permissionArr[1]);
        this.isOtherEmpty = this.isEmptyPermission(permissionArr[2]);
        this.isEmpty = this.isUserEmpty && this.isGroupEmpty && this.isOtherEmpty;
    }

    /**
     * 当前权限是否为空
     */
    isEmptyPermission(permissionStr: string): boolean {
        // '0'或者不存在都表示空
        return permissionStr === '0' || permissionStr === undefined;
    }

    /**
     * 根据传入值渲染当前勾选框初始值
     */
    renderDefaultValue(permission?: string): string[] {
        if (!permission) {
            return [];
        }

        // 将权限转换成二进制字符串，1表示选中，0表示没选中
        const bins = Number(permission).toString(2).split('').reverse();
        const formValueArr = bins.reduce((hexRadix, bin, idx) => {
            return Number(bin) === 1 ? hexRadix.concat(idx) : hexRadix;
        }, []);

        return formValueArr.reverse();
    }

    /**
     * 返回表单值
     * 读取：4，写入：2，可执行：1，相加后下发
     */
    getVmValue(): string {
        let value = '';
        const controls = ['user', 'userGroup', 'other'];

        controls.map((control: string): void => {
            let singleFormControlValue = 0;

            this.vm.value[control]?.map((checkValue: number) => {
                singleFormControlValue += Math.pow(2, checkValue);
            });

            value = value.concat(String(singleFormControlValue));
        });

        return value;
    }

    isModified(): boolean {
        return this.vm?.valueChanged;
    }
}
